import { useEffect } from "react";
import { SAME_AS, CANON } from "../data/nap";
import { getContent } from "../context/ContentContext";
import { HOURS_SPEC, TEL_MAIN, POSTAL_ADDRESS, GEO, SERVES_CUISINE } from "../data/nap";

let SEO_CFG = { mode: "live", canonical_host: null };
try { SEO_CFG = { ...SEO_CFG, ...(JSON.parse(localStorage.getItem("urban_seo_cfg")) || {}) }; } catch { /* noop */ }

export const seoCfg = () => SEO_CFG;

export function setSeoCfg(cfg) {
  SEO_CFG = { ...SEO_CFG, ...cfg };
  try { localStorage.setItem("urban_seo_cfg", JSON.stringify(cfg)); } catch { /* noop */ }
}

export function ownerCanonical(path) {
  const host = "https://tryurbanturban.com";
  if (path.startsWith("/store/") || path === "/menu" || path.startsWith("/menu/")) return `${host}/menu`;
  if (path.startsWith("/locations")) return `${host}/chicago`;
  return `${host}/`;
}

export function modeCanonical(path) {
  if (SEO_CFG.mode === "premigration") return ownerCanonical(path);
  const base = SEO_CFG.canonical_host || CANON || window.location.origin;
  const clean = path.length > 1 ? path.replace(/\/+$/, "") : path;
  return `${base}${clean}`;
}

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) { if (el && el.dataset.seo) el.remove(); return; }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.dataset.seo = "1";
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function usePageSeo({ title, description, canonicalPath, robots, jsonLd, ogImage }) {
  const path = (canonicalPath || window.location.pathname).replace(/\/+$/, "") || "/";
  const override = getContent(`seo:${path}`) || {};
  if (override.title) title = override.title;
  const cleanMeta = (s) => (!s || typeof s !== "string") ? s : s.replace(/ — /g, ": ").replace(/—/g, "-").replace(/\bBest\s+/g, "").replace(/Indo-Chinese wings/g, "street-style chilli garlic wings").replace(/Indo-Chinese/g, "street-style").replace(/Chinese/g, "street-style");
  title = cleanMeta(title);
  description = cleanMeta(description);
  if (override.description) description = override.description;
  useEffect(() => {
    if (title) document.title = title;
    const premig = SEO_CFG.mode === "premigration";
    setMeta("name", "description", description);
    setMeta("name", "robots", premig ? "noindex,nofollow" : robots);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", ogImage ? (ogImage.startsWith("http") ? ogImage : `${window.location.origin}${ogImage}`) : null);
    setMeta("property", "og:url", canonicalPath ? `${window.location.origin}${canonicalPath}` : null);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage ? (ogImage.startsWith("http") ? ogImage : `${window.location.origin}${ogImage}`) : null);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = modeCanonical(canonicalPath || window.location.pathname);
    if (canonicalPath) setMeta("property", "og:url", link.href);

    let script = document.getElementById("seo-jsonld");
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "seo-jsonld";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }

    return () => {
      setMeta("name", "robots", null);
      setMeta("property", "og:image", null);
      const s = document.getElementById("seo-jsonld");
      if (s) s.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonicalPath, robots, ogImage, JSON.stringify(jsonLd || null)]);
}

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `https://tryurbanturban.com${path}` })),
});

export const orgJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Urban Turban by VeerGi",
  url: window.location.origin,
  logo: `${window.location.origin}/logo-emblem.png`,
  sameAs: SAME_AS,
});

export const restaurantJsonLd = (loc) => ({
  "@context": "https://schema.org",
  "@type": "Restaurant",
  name: "Urban Turban by VeerGi",
  servesCuisine: SERVES_CUISINE,
  address: POSTAL_ADDRESS,
  geo: GEO,
  url: `${window.location.origin}/locations/${loc.slug}`,
  telephone: TEL_MAIN,
  openingHoursSpecification: loc.status === "active" ? HOURS_SPEC : undefined,
  menu: `${window.location.origin}/store/restaurant`,
  acceptsReservations: loc.status === "active" ? true : undefined,
});
